// Sugar Palette - Library of all available sugars organized by category
// Allows users to click sugars to add to their pattern

import { useState } from 'react';
import { Tab, Input, Icon, Button } from 'semantic-ui-react';
import SNFGsvg from '../../visualizer-tool/glycan-interpreter/snfgSVG';
import resdict from '../../visualizer-tool/data/resdict.json';
import '../../css/advanced-search.css';

// Organize sugars by category
const SUGAR_CATEGORIES = {
  common: ['Glc', 'Man', 'Gal', 'GlcA', 'GlcNAc', 'ManNAc', 'GalNAc', 'Fuc', 'Rha', 'Neu5Ac', 'Kdo', 'GalA'],
  hexoses: ['Glc', 'Man', 'Gal', 'Gul', 'Alt', 'All', 'Tal', 'Ido', 'Galf'],
  hexNAc: ['GlcNAc', 'ManNAc', 'GalNAc', 'GulNAc', 'AltNAc', 'AllNAc', 'TalNAc', 'IdoNAc', 'GlcNAcA', 'ManNAcA'],
  uronic: ['GlcA', 'ManA', 'GalA', 'GulA', 'AltA', 'AllA', 'TalA', 'IdoA'],
  deoxy: ['Fuc', 'Rha', 'Qui', '6dGul', '6dAlt', '6dTal'],
  pentoses: ['Ara', 'Lyx', 'Xyl', 'Rib', 'Ribf'],
  sialic: ['Neu5Ac', 'Neu5Gc', 'Kdn', 'Neu', 'Sia'],
  other: ['Kdo', 'Bac', 'LDmanHep', 'DDmanHep', 'MurNAc', 'Api', 'Fru', 'Pse', 'Leg']
};

export default function SugarPalette({ onSelectSugar, onAddWildcard }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('common');

  // Get sugars for current category
  const getCurrentCategorySugars = () => {
    const category = SUGAR_CATEGORIES[activeCategory] || [];
    return category.filter(sugar => resdict[sugar]);
  };

  // Filter sugars by search query
  const getFilteredSugars = () => {
    if (!searchQuery) {
      return getCurrentCategorySugars();
    }

    const query = searchQuery.toLowerCase();
    return getCurrentCategorySugars().filter(sugar =>
      sugar.toLowerCase().includes(query)
    );
  };

  // Search all sugars across categories
  const searchAllSugars = () => {
    if (!searchQuery) return [];

    const query = searchQuery.toLowerCase();
    return Object.keys(resdict).filter(sugar =>
      sugar.toLowerCase().includes(query)
    );
  };

  const filteredSugars = searchQuery ? searchAllSugars() : getFilteredSugars();

  // Category tabs
  const categoryTabs = [
    {
      menuItem: { key: 'common', content: 'Common', icon: 'star' },
      category: 'common'
    },
    {
      menuItem: { key: 'hexoses', content: 'Hexoses', icon: 'circle outline' },
      category: 'hexoses'
    },
    {
      menuItem: { key: 'hexNAc', content: 'HexNAc', icon: 'square outline' },
      category: 'hexNAc'
    },
    {
      menuItem: { key: 'uronic', content: 'Uronic', icon: 'diamond outline' },
      category: 'uronic'
    },
    {
      menuItem: { key: 'deoxy', content: 'Deoxy', icon: 'caret up' },
      category: 'deoxy'
    },
    {
      menuItem: { key: 'pentoses', content: 'Pentoses', icon: 'star outline' },
      category: 'pentoses'
    },
    {
      menuItem: { key: 'sialic', content: 'Sialic', icon: 'diamond' },
      category: 'sialic'
    },
    {
      menuItem: { key: 'other', content: 'Other', icon: 'ellipsis horizontal' },
      category: 'other'
    }
  ];

  return (
    <div className="sugar-palette">
      <div className="palette-controls">
        <Input
          icon="search"
          placeholder="Search sugars..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="sugar-search-input"
        />

        <Button
          onClick={onAddWildcard}
          className="wildcard-button"
          icon
          labelPosition="left"
        >
          <Icon name="asterisk" />
          Add Wildcard (*)
        </Button>
      </div>

      <div className="category-tabs">
        {categoryTabs.map((tab, index) => (
          <button
            key={tab.menuItem.key}
            className={`category-tab ${activeCategory === tab.category ? 'active' : ''}`}
            onClick={() => setActiveCategory(tab.category)}
          >
            <Icon name={tab.menuItem.icon} />
            {tab.menuItem.content}
          </button>
        ))}
      </div>

      <div className="sugar-grid">
        {filteredSugars.length === 0 ? (
          <div className="no-sugars-message">
            <Icon name="search" size="large" />
            <p>No sugars found matching "{searchQuery}"</p>
          </div>
        ) : (
          filteredSugars.map((sugarName) => (
            <div
              key={sugarName}
              className="sugar-grid-item"
              onClick={() => onSelectSugar(sugarName)}
              title={`Click to add ${sugarName}`}
            >
              <div className="sugar-shape-container">
                <SNFGsvg snfg={sugarName} />
              </div>
              <div className="sugar-label">{sugarName}</div>
            </div>
          ))
        )}
      </div>

      {searchQuery && (
        <div className="search-results-info">
          <Icon name="info circle" />
          {filteredSugars.length} sugar{filteredSugars.length !== 1 ? 's' : ''} found
        </div>
      )}
    </div>
  );
}
